/**
 * ═══════════════════════════════════════════════════════════════
 * Configuración de visibilidad por estatus
 *
 * Define qué secciones y datos se muestran en la vista de detalle
 * según el estado actual de la cotización.
 *
 * ESTADOS:
 * 1 - NUEVO:              Datos básicos de cotización, rutas y estructuras (sin precios, sin km, sin trazos)
 * 2 - EN PLANEACIÓN:      + Trazos, paradas, horarios, mapas, kilometraje
 * 3 - EN COSTEO:          + Costos y precios (se están editando)
 * 4 - ESPERANDO APROB.:   Todo visible, costos finalizados
 * 5 - APROBADO:           Todo visible
 * 6 - EN PRODUCCIÓN:      Todo visible
 *
 * Cada estatus solo declara lo que difiere de BASE. Si agregas un
 * flag nuevo, solo tienes que pensar en qué estatus se comporta
 * distinto al default — no en los 6-8 estatus uno por uno.
 * ═══════════════════════════════════════════════════════════════
 */

const BASE = {
    infoGeneral: true,
    rutas: true,
    rutaDatosBasicos: true,
    estructuras: true,
    equipamiento: true,
    observaciones: true,
    kilometraje: true,
    trazos: true,
    paradas: true,
    mapa: true,
    costos: true,
    precioCliente: true,
    pagoChofer: true,
    pagoSubcontratista: true,
    puedeEditar: false,
    puedeEditarTrazo: false,
    puedeEditarCostos: false,
    mensajeContextual: null,
    enviarAProduccion: false,
};

const VISIBILIDAD_POR_ESTATUS = {
    1: {
        // NUEVO - Solo datos básicos, sin costos ni kilometraje
        ...BASE,
        kilometraje: false,
        paradas: false,
        costos: false,
        precioCliente: false,
        pagoChofer: false,
        pagoSubcontratista: false,
        puedeEditar: true,
        puedeEditarTrazo: true,
        mensajeContextual: "Esta cotización está recién creada. Aún no se han definido trazos ni costos.",
    },
    2: {
        // EN PLANEACIÓN - + trazos, paradas, mapas, km
        ...BASE,
        costos: false,
        precioCliente: false,
        pagoChofer: false,
        pagoSubcontratista: false,
        puedeEditarTrazo: true,
        mensajeContextual: "Se están definiendo los trazos y paradas de las rutas.",
    },
    3: {
        // EN COSTEO - + todos los costos, editables
        ...BASE,
        puedeEditarCostos: true,
        mensajeContextual: "Se están definiendo los precios y pagos de cada ruta.",
    },
    4: {
        // ESPERANDO APROBACIÓN - Todo visible, solo lectura
        ...BASE,
        mensajeContextual: "La cotización fue enviada al cliente y está esperando su aprobación.",
    },
    5: {
        // APROBADO - Todo visible, costos aún editables, listo para producción
        ...BASE,
        puedeEditarCostos: true,
        enviarAProduccion: true,
        mensajeContextual: "La cotización fue aprobada y está esperando su envío a producción.",
    },
    6: {
        // EN PRODUCCIÓN - Todo visible, solo lectura
        ...BASE,
    },
};

// El estatus 9 (Costeado) se comporta igual que 3 (En Costeo)
VISIBILIDAD_POR_ESTATUS[9] = VISIBILIDAD_POR_ESTATUS[3];

// El estatus 8 (Solicitud de Cambios) se comporta igual que 2 (En Planeación)
VISIBILIDAD_POR_ESTATUS[8] = VISIBILIDAD_POR_ESTATUS[2];

const VISIBILIDAD_DEFAULT = VISIBILIDAD_POR_ESTATUS[1];

/**
 * Obtiene la configuración de visibilidad para un estatus dado.
 * @param {number} estatus
 * @returns {Object}
 */
export const getVisibilidad = (estatus) => {
    return VISIBILIDAD_POR_ESTATUS[estatus] || VISIBILIDAD_DEFAULT;
};

/**
 * Verifica si un campo/sección específica es visible para un estatus.
 * @param {number} estatus
 * @param {string} seccion
 * @returns {boolean}
 */
export const esVisible = (estatus, seccion) => {
    const config = getVisibilidad(estatus);
    return config[seccion] === true;
};

export default VISIBILIDAD_POR_ESTATUS;
